/**
 * Invalidate a query the moment a known deadline passes (the slate's next midnight, the quest
 * week's end), so Home rolls over without waiting for the next poll (specs/home-page AC-9, AC-22).
 */

import { useEffect } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

/** setTimeout's ceiling; a deadline further out is re-armed on the next render anyway. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
/** Small grace so the server has crossed the boundary too. */
const GRACE_MS = 1000;

export function useRefetchAt(deadlineIso: string | null | undefined, queryKey: QueryKey): void {
  const queryClient = useQueryClient();
  const key = JSON.stringify(queryKey);

  useEffect(() => {
    if (!deadlineIso) return;
    const deadline = new Date(deadlineIso).getTime();
    if (Number.isNaN(deadline)) return;
    const delay = Math.min(Math.max(deadline - Date.now() + GRACE_MS, 0), MAX_TIMEOUT_MS);
    const timer = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: JSON.parse(key) as QueryKey });
    }, delay);
    return () => clearTimeout(timer);
  }, [deadlineIso, key, queryClient]);
}
